import React, { useState } from 'react';
import Slack from '@svg/slack';
import styled from 'styled-components';

import { LoadingButton } from '@common/Button';
import { SettingsOutlined, TagOutlined } from '@mui/icons-material';
import { Card } from '@common/Card';
import { Label } from '@common/Label';
import { LinearProgress, Stack } from '@mui/material';
import { DocumentLoader } from '@common/hooks/useDocument';
import { useTheme } from '../../../../../../../styles';
import { CheckDeleteFileParams, deleteSlackLoader, LoaderConfiguration } from '@requests/loaders';
import { formatCronExpression } from '@common/CronSchedule';
import { SubText } from '@common/SubText/SubText';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { State } from '@store/index';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  p,
  pre {
    margin: 0;
    color: ${({ theme }) => theme.text.standard};
    font-size: 14px;
  }

  #channel-name {
    max-width: 340px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

interface Props {
  open: string | null;
  loader: LoaderConfiguration | undefined;
  page: number;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  documentLoader: DocumentLoader;
  setTab: React.Dispatch<React.SetStateAction<string>>;
  refreshLoader: ({ service_id, bucket_name }: { service_id: string; bucket_name: string }) => void;
  setRemoveLoader: React.Dispatch<React.SetStateAction<string | null>>;
  checkForDeletedFiles: ({ service_id, bucket_name, files }: CheckDeleteFileParams) => void;
}

export const SlackLoader: React.FC<Props> = ({ loader, open, setOpen, page }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const userId = useSelector((state: State) => state.auth.user_id);
  const orgId = useSelector((state: State) => state.auth.org_id);

  const { service_id, bucketName } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [collapsed, setCollapsed] = useState(true);

  const { mutateAsync: deleteLoader, isPending: isDeleteLoading } = useMutation({
    mutationFn: deleteSlackLoader,
    onSuccess: () => {
      enqueueSnackbar('Loader removed from the bucket successfully.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['globalFiles', 'bucket-loader'] });
    },
    onError: (error) => {
      enqueueSnackbar(error.message || 'An unexpected error occured when deleting the loader.', { variant: 'error' });
    },
  });

  const handleDeleteLoader = async () => {
    try {
      await deleteLoader({
        service_id: service_id as string,
        bucket_name: bucketName as string,
        user_id: userId,
        org_id: orgId,
      });
    } catch {}
  };

  const isLoaderConfigured = loader && loader.loader === 'slack';
  const isLoaderJobRunning = isLoaderConfigured && !loader?.last_refreshed;

  const expression = formatCronExpression(loader?.refresh_interval_config.value || '');
  const cronExpression =
    loader?.refresh_interval_config.value.split(' ').length === 5 ? `*   ${expression}` : expression;

  const cronDescription = [
    'Click "Settings" at the bottom of the loader to view and change this schedule.\n',
    '┌──────────────────────── second (optional)',
    '│   ┌──────────────────── minute',
    '│   │   ┌──────────────── hour',
    '│   │   │   ┌──────────── day of month',
    '│   │   │   │   ┌──────── month',
    '│   │   │   │   │   ┌──── day of week',
    '│   │   │   │   │   │',
    '│   │   │   │   │   │\n',
    cronExpression,
  ].join('\n');

  if (isLoaderJobRunning) {
    const body = (
      <Container>
        <Stack spacing='8px'>
          <Label
            title='Authentication'
            tooltip='This is the secret being used to authenticate requests to the Slack API.'
          />
          <pre>{loader.auth_secret_name}</pre>
        </Stack>
        <Stack spacing='8px'>
          <Label title='Started At' tooltip='This is a timestamp of the when the job was started.' />
          <p>{new Date(loader.created_at).toLocaleString()}</p>
        </Stack>
        <Stack spacing='8px'>
          <Label title='Channels' tooltip='This is the list of Slack channels being used to pull messages from.' />
          <Stack spacing={1.25}>
            {loader.loader_settings.channels.map((item) => (
              <Stack spacing={0.25} key={item.channel}>
                <Stack spacing={0.25} key={item.channel} direction='row' alignItems='center'>
                  <TagOutlined sx={{ fontSize: '2ch' }} />
                  <p id='channel-name'>{item.channel}</p>
                </Stack>
                {(item.status === 'running' || item.status === 'initiating') && <LinearProgress color={'primary'} />}
                {item.status === 'failed' && <LinearProgress color={'error'} variant='determinate' value={100} />}
                {item.status !== 'running' && item.status !== 'failed' && item.status !== 'initiating' && (
                  <LinearProgress color={'success'} variant='determinate' value={100} />
                )}
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Container>
    );

    return (
      <Card
        title='Slack'
        description={`The Slack loader is currently running a job to pull messages from ${loader.loader_settings.channels.length} Slack channel(s). This process may take a few minutes to complete.`}
        icon={<Slack color={theme.text.standard} height={21} />}
        link='https://api.slack.com/messaging/retrieving'
        body={body}
        actions={
          <Stack direction='row' spacing={1}>
            <LoadingButton fullWidth onClick={handleDeleteLoader} loading={isDeleteLoading} minHeight='33px'>
              Cancel Loader
            </LoadingButton>
          </Stack>
        }
      />
    );
  }

  const getTimeSinceRefreshStarted = () => {
    if (loader?.loader !== 'slack' || !loader?.last_refreshed?.start_time) return null;

    const startTime = loader.last_refreshed.start_time;
    const currentTime = new Date().toISOString();

    const timeDiff = new Date(currentTime).getTime() - new Date(startTime).getTime();
    const minutes = Math.floor(timeDiff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  };

  if (isLoaderConfigured) {
    const body = (
      <Container>
        <Stack spacing='8px'>
          <Label
            title='Authentication'
            tooltip='This is the secret being used to authenticate requests to the Slack API.'
          />
          <pre>{loader.auth_secret_name}</pre>
        </Stack>
        <Stack spacing='8px'>
          <Label title='Refresh Every' tooltip={cronDescription} />
          <p>{`${loader.refresh_interval_config?.value}`}</p>
        </Stack>
        {loader.loader === 'slack' && (
          <Stack spacing='8px'>
            <Label title='Last Refreshed' tooltip='This is a timestamp of the last time the files were refreshed.' />
            <p>
              {loader.last_refreshed?.start_time
                ? new Date(loader.last_refreshed?.start_time).toLocaleString()
                : 'Never'}
            </p>
          </Stack>
        )}
        <Stack spacing='8px'>
          <Label
            title='Next Refresh'
            tooltip='This is a timestamp of the next scheduled execution time for this loader.'
          />
          <p>{new Date(loader.next_refresh).toLocaleString()}</p>
        </Stack>
        <Stack spacing='8px'>
          <Label title='Channels' tooltip='This is the list of Slack channels being used to pull messages from.' />
          <Stack spacing={1.25}>
            {loader.loader_settings.channels.map((item) => (
              <Stack spacing={0.25} key={item.channel} direction='row' alignItems='center'>
                <TagOutlined sx={{ fontSize: '2ch' }} />
                <p id='channel-name'>{item.channel}</p>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Container>
    );

    const totalChannels = loader?.loader_settings?.channels?.length;
    const activeChannels = loader?.loader_settings?.channels?.filter(
      (channel) => channel?.status !== 'running'
    )?.length;

    return (
      <Card
        title='Slack'
        description='The Slack loader is configured for this bucket. This loader will handle the refreshing of all content.'
        icon={<Slack color={theme.text.standard} height={21} />}
        link='https://api.slack.com/messaging/retrieving'
        body={body}
        actions={
          <Stack spacing={2}>
            {loader.loader === 'slack' && loader.last_refreshed?.status === 'running' && (
              <Stack spacing={1}>
                <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                  <SubText>Refreshing In Progress</SubText>
                  <SubText>{`${getTimeSinceRefreshStarted()} ago (${activeChannels}/${totalChannels})`}</SubText>
                </Stack>
                <LinearProgress />
              </Stack>
            )}
            <Stack direction='row' spacing={1}>
              <LoadingButton fullWidth onClick={handleDeleteLoader} loading={isDeleteLoading} minHeight='33px'>
                Delete Loader
              </LoadingButton>
            </Stack>
          </Stack>
        }
      />
    );
  }

  return (
    <Card
      title='Slack'
      description='Load messages from Slack channels.'
      icon={<Slack color={theme.text.standard} height={21} />}
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      link='https://api.slack.com/messaging/retrieving'
      actions={
        <LoadingButton
          minHeight='32px'
          fullWidth
          loading={page > 0 && open === 'slack'}
          onClick={() => setOpen('slack')}
          customColor='black'
          startIcon={<SettingsOutlined sx={{ fontSize: '2.1ch !important' }} />}
        >
          Configure
        </LoadingButton>
      }
    />
  );
};
